"use client";

/**
 * Memory tool calls in the thread's activity list.
 *
 * Display-only version of the web app's memory rows: a group of consecutive
 * calls to one memory tool collapses into a single line ("Remembered 2
 * things for this bid"), with the entries listed underneath. The web app's
 * Keep / Not now decisions and the memory dialog need its memory API client
 * and toasts, which the pane does not carry yet; memories can still be
 * managed from the web app's Memory tab.
 */

import type { FC } from "react";

import {
  ToolActivityRow,
  type ToolActivity,
  type ToolActivityPart,
  type ToolActivityStatus
} from "@/agents-v2/components/assistant-ui/tool-activity-row";
import {
  FORGET_TOOL_NAME,
  MEMORY_ACTIVITY_TOOL_NAMES,
  REMEMBER_TOOL_NAME,
  REVISE_TOOL_NAME,
  SET_BID_VALUES_TOOL_NAME,
  USE_MEMORIES_TOOL_NAME
} from "@/agents-v2/memory/tool-names";
import { asArray, asRecord, getStringValue, parseMaybeJson } from "@/agents-v2/utils/unknown";

/** What the activity list hands a memory group. */
export interface MemoryActivityProps {
  parts: ToolActivityPart[];
  activity: ToolActivity;
  status: ToolActivityStatus;
}

const isMemoryToolName = (
  toolName: string
): toolName is (typeof MEMORY_ACTIVITY_TOOL_NAMES)[number] =>
  (MEMORY_ACTIVITY_TOOL_NAMES as readonly string[]).includes(toolName);

/**
 * Whether a tool part is one the memory rows render.
 * @param part - A tool-call part.
 * @returns True for the memory write and use tools.
 */
export const isMemoryActivityPart = (part: ToolActivityPart): boolean =>
  isMemoryToolName(part.toolName);

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

/** The one-line statements a group of calls wrote, read or removed. */
const statementsOf = (parts: readonly ToolActivityPart[]): string[] => {
  const statements: string[] = [];
  for (const part of parts) {
    const args = asRecord(parseMaybeJson(part.args ?? part.argsText));
    const result = asRecord(parseMaybeJson(part.result));
    const fromArgs = getStringValue(args, "statement") ?? getStringValue(args, "reason");
    if (fromArgs) statements.push(fromArgs);
    for (const entry of asArray(result.entries ?? result.memories ?? result.used)) {
      const statement = getStringValue(asRecord(entry), "statement");
      if (statement) statements.push(statement);
    }
  }
  return statements;
};

const titleFor = (toolName: string, count: number, status: ToolActivityStatus): string | null => {
  if (status !== "complete") return null;
  switch (toolName) {
    case REMEMBER_TOOL_NAME:
      return count > 1 ? `Remembered ${count} things` : null;
    case REVISE_TOOL_NAME:
      return count > 1 ? `Updated ${count} memories` : null;
    case FORGET_TOOL_NAME:
      return count > 1 ? `Removed ${count} memories` : null;
    case USE_MEMORIES_TOOL_NAME:
      return count > 0 ? `Used ${count} ${plural(count, "memory", "memories")}` : null;
    case SET_BID_VALUES_TOOL_NAME:
      return null;
    default:
      return null;
  }
};

/**
 * The row for one group of consecutive calls to the same memory tool.
 * @param props - The group, its labels and its status.
 * @returns The row with its entries underneath.
 */
export const MemoryActivityGroup: FC<MemoryActivityProps> = ({ parts, activity, status }) => {
  const { toolName } = parts[0];
  if (!isMemoryToolName(toolName)) return null;
  const statements = statementsOf(parts);
  const title = titleFor(toolName, statements.length || parts.length, status);
  return (
    <div data-slot="memory-activity" className="w-full">
      <ToolActivityRow
        runningLabel={activity.runningLabel}
        completeLabel={title ?? activity.completeLabel}
        errorLabel={activity.errorLabel}
        icon={activity.icon}
        status={status}
        open={false}
        hasDetails={false}
      />
      {status === "complete" && statements.length > 0 && (
        <ul data-slot="memory-activity-entries" className="w-full pl-7 text-xs text-muted-foreground">
          {statements.slice(0, 6).map((statement, index) => (
            <li key={`${index}-${statement}`} className="truncate py-0.5">
              {statement}
            </li>
          ))}
          {statements.length > 6 && <li className="py-0.5">…and {statements.length - 6} more</li>}
        </ul>
      )}
    </div>
  );
};
